<div class="main-view space-y-2 md:space-y-4">
  <app-page-surface-header
    titleIcon="chart"
    title="AI / LLM Cost"
    subtitle="What the platform spends on AI, by tenant, model and PD. Every figure is priced from the tokens the provider actually reported."
  />

  <div class="panel">
    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div class="flex gap-2">
        @for (preset of presets; track preset.key) {
        <button
          type="button"
          [class]="selectedPreset === preset.key ? btn.smallPrimary : btn.smallSecondary"
          (click)="onPresetChange(preset.key)"
        >
          {{ preset.label }}
        </button>
        }
      </div>
      <a routerLink="/llm-usage" [class]="btn.smallSecondary">View every call</a>
    </div>

    <div class="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
      <select class="form-select" [(ngModel)]="filters.tenant_id" (ngModelChange)="applyFilters()">
        <option [ngValue]="undefined">All tenants</option>
        @for (option of tenantOptions; track option.id) {
        <option [ngValue]="option.id">{{ option.name }}</option>
        }
      </select>
      <select class="form-select" [(ngModel)]="filters.model_name" (ngModelChange)="applyFilters()">
        <option [ngValue]="undefined">All models</option>
        @for (option of modelOptions; track option) {
        <option [ngValue]="option">{{ option }}</option>
        }
      </select>
      <input type="text" class="form-input" placeholder="Task code" [(ngModel)]="filters.task_code"
        (keyup.enter)="applyFilters()" />
      <select class="form-select" [(ngModel)]="filters.source" (ngModelChange)="applyFilters()">
        <option [ngValue]="undefined">All sources</option>
        @for (source of sources; track source) {
        <option [ngValue]="source">{{ source }}</option>
        }
      </select>
      <button type="button" [class]="btn.smallSecondary" (click)="resetFilters()" [disabled]="!hasFilters">
        Clear filters
      </button>
    </div>

    @if (error) {
    <div class="mb-4 rounded border border-danger/40 bg-danger-light p-3 text-danger">{{ error }}</div>
    } @if (loading) {
    <div class="py-10 text-center text-white-dark">Loading cost data…</div>
    } @else if (totals) {
    <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
      <div class="panel bg-primary-light">
        <div class="text-xs uppercase text-white-dark">Total LLM Cost</div>
        <div class="mt-1 text-2xl font-semibold">{{ formatUsd(totals.total_cost_usd) }}</div>
        <div class="mt-1 text-xs text-white-dark">₹{{ totals.total_cost_inr }}</div>
      </div>
      <div class="panel">
        <div class="text-xs uppercase text-white-dark">PDs Processed</div>
        <div class="mt-1 text-2xl font-semibold">{{ totals.pds | number }}</div>
        <div class="mt-1 text-xs text-white-dark">{{ formatUsd(totals.avg_cost_per_pd_usd) }} avg / PD</div>
      </div>
      <div class="panel">
        <div class="text-xs uppercase text-white-dark">LLM Calls</div>
        <div class="mt-1 text-2xl font-semibold">{{ totals.calls | number }}</div>
        <div class="mt-1 text-xs text-white-dark">{{ formatUsd(totals.avg_cost_per_call_usd) }} avg / call</div>
      </div>
      <div class="panel">
        <div class="text-xs uppercase text-white-dark">Total Tokens</div>
        <div class="mt-1 text-2xl font-semibold">{{ compactTokens(totals.total_tokens) }}</div>
        <div class="mt-1 text-xs text-white-dark">
          {{ compactTokens(totals.input_tokens) }} in · {{ compactTokens(totals.output_tokens) }} out
        </div>
      </div>
    </div>

    <!-- Failed and possibly-billed calls are shown, not hidden: a timed-out call may still
         have been charged, and burying it understates spend. -->
    @if (totals.failed_calls || totals.unknown_billed_calls) {
    <div class="mt-3 flex flex-wrap gap-4 text-xs">
      @if (totals.failed_calls) {
      <span class="badge bg-danger-light text-danger">{{ totals.failed_calls | number }} failed</span>
      } @if (totals.unknown_billed_calls) {
      <span class="badge bg-warning-light text-warning">
        {{ totals.unknown_billed_calls | number }} timed out — may still have been billed
      </span>
      }
    </div>
    }
    }
  </div>

  @if (!loading && trend.length) {
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <div class="panel">
      <h5 class="mb-3 text-lg font-semibold">Daily cost</h5>
      <apx-chart
        [series]="costChart.series"
        [chart]="costChart.chart"
        [dataLabels]="costChart.dataLabels"
        [stroke]="costChart.stroke"
        [xaxis]="costChart.xaxis"
        [yaxis]="costChart.yaxis"
        [colors]="costChart.colors"
        [tooltip]="costChart.tooltip"
        [grid]="costChart.grid"
      />
    </div>
    <div class="panel">
      <h5 class="mb-3 text-lg font-semibold">Daily tokens</h5>
      <apx-chart
        [series]="tokenChart.series"
        [chart]="tokenChart.chart"
        [dataLabels]="tokenChart.dataLabels"
        [plotOptions]="tokenChart.plotOptions"
        [xaxis]="tokenChart.xaxis"
        [colors]="tokenChart.colors"
        [grid]="tokenChart.grid"
      />
    </div>
  </div>
  } @else if (!loading) {
  <div class="panel py-8 text-center text-white-dark">
    No daily series yet — the rollup runs nightly, so today's calls appear tomorrow.
  </div>
  }

  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <div class="panel">
      <h5 class="mb-1 text-lg font-semibold">Cost by agent</h5>
      <div class="mb-3 text-xs text-white-dark">Which agent's PDs cost the most to score.</div>
      <div class="table-responsive">
        <table class="table-hover">
          <thead><tr><th>Agent</th><th class="text-right">PDs</th><th class="text-right">Calls</th><th class="text-right">Cost</th><th class="text-right">Avg / PD</th></tr></thead>
          <tbody>
            @for (row of agents; track row.agent_id) {
            <tr>
              <td>{{ row.agent_name }}</td>
              <td class="text-right">{{ row.pds | number }}</td>
              <td class="text-right">{{ row.calls | number }}</td>
              <td class="text-right font-semibold">{{ formatUsd(row.total_cost_usd) }}</td>
              <td class="text-right">{{ formatUsd(row.avg_cost_per_pd_usd) }}</td>
            </tr>
            } @empty {
            <tr><td colspan="5" class="py-6 text-center text-white-dark">No usage in this period.</td></tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <div class="panel">
      <h5 class="mb-1 text-lg font-semibold">Cost by workflow</h5>
      <!-- Keyed on the exact flow version that ran, so a workflow renamed since still reports
           against the version the session actually used. -->
      <div class="mb-3 text-xs text-white-dark">Which journey drives AI spend, by the flow version that ran.</div>
      <div class="table-responsive">
        <table class="table-hover">
          <thead><tr><th>Workflow</th><th class="text-right">PDs</th><th class="text-right">Calls</th><th class="text-right">Cost</th><th class="text-right">Avg / PD</th></tr></thead>
          <tbody>
            @for (row of workflows; track row.flow_definition_id) {
            <tr>
              <td class="break-all text-xs">{{ row.workflow_name }}</td>
              <td class="text-right">{{ row.pds | number }}</td>
              <td class="text-right">{{ row.calls | number }}</td>
              <td class="text-right font-semibold">{{ formatUsd(row.total_cost_usd) }}</td>
              <td class="text-right">{{ formatUsd(row.avg_cost_per_pd_usd) }}</td>
            </tr>
            } @empty {
            <tr><td colspan="5" class="py-6 text-center text-white-dark">No usage in this period.</td></tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <div class="panel">
    <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div>
        <h5 class="text-lg font-semibold">AI cost against revenue</h5>
        <div class="text-xs text-white-dark">
          Thinnest margin first. Revenue is what each tenant's PDs were billed at; AI cost is
          converted at the rate captured on each call, not today's.
        </div>
      </div>
      <button type="button" [class]="btn.smallSecondary" (click)="exportMargin()" [disabled]="!margin.length">
        Export CSV
      </button>
    </div>
    <div class="table-responsive">
      <table class="table-hover">
        <thead>
          <tr><th>Tenant</th><th class="text-right">Billed PDs</th><th class="text-right">Revenue</th><th class="text-right">AI Cost</th><th class="text-right">Margin</th><th class="text-right">Margin %</th><th class="text-right">AI % of rev</th></tr>
        </thead>
        <tbody>
          @for (row of margin; track row.tenant_id) {
          <tr>
            <td>
              {{ row.tenant_name }}
              <!-- Cost that could not be priced understates cost, which OVERSTATES margin —
                   the direction that makes an operator relax. Never shown silently. -->
              @if (!row.cost_is_complete) {
              <span class="ml-1 badge bg-warning-light text-warning" title="Some calls had no price, or none were recorded — this margin is a ceiling.">
                partial cost
              </span>
              } @if (row.currency_mismatch) {
              <span class="ml-1 badge bg-warning-light text-warning" title="This tenant has revenue in more than one currency; only the INR portion is counted.">
                mixed currency
              </span>
              }
            </td>
            <td class="text-right">{{ row.billed_pds | number }}</td>
            <td class="text-right">₹{{ row.revenue_inr }}</td>
            <td class="text-right">₹{{ row.ai_cost_inr }}</td>
            <td class="text-right font-semibold" [class.text-danger]="+row.margin_inr < 0">₹{{ row.margin_inr }}</td>
            <td class="text-right">{{ row.margin_pct }}%</td>
            <td class="text-right">{{ row.ai_cost_pct_of_revenue }}%</td>
          </tr>
          } @empty {
          <tr><td colspan="7" class="py-6 text-center text-white-dark">No billed PDs in this period.</td></tr>
          }
        </tbody>
      </table>
    </div>
    @if (+unattributedCostInr > 0) {
    <div class="mt-3 text-xs text-white-dark">
      ₹{{ unattributedCostInr }} of AI spend in this period belongs to no tenant (simulate, preview
      or health traffic). It is real money, so it is reported here rather than folded into a tenant.
    </div>
    }
  </div>

  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <div class="panel">
      <div class="mb-3 flex items-center justify-between">
        <h5 class="text-lg font-semibold">Cost by tenant</h5>
        <button type="button" [class]="btn.smallSecondary" (click)="exportTenants()" [disabled]="!tenants.length">
          Export CSV
        </button>
      </div>
      <div class="table-responsive">
        <table class="table-hover">
          <thead>
            <tr><th>Tenant</th><th class="text-right">PDs</th><th class="text-right">Calls</th><th class="text-right">Tokens</th><th class="text-right">Cost</th><th class="text-right">Avg / PD</th></tr>
          </thead>
          <tbody>
            @for (row of tenants; track row.tenant_id) {
            <tr>
              <td>
                @if (row.tenant_id !== null) {
                <a [routerLink]="['/llm-tenant', row.tenant_id]" class="text-primary hover:underline">{{ row.tenant_name }}</a>
                } @else {
                {{ row.tenant_name }}
                }
              </td>
              <td class="text-right">{{ row.pds | number }}</td>
              <td class="text-right">{{ row.calls | number }}</td>
              <td class="text-right">{{ compactTokens(row.total_tokens) }}</td>
              <td class="text-right font-semibold">{{ formatUsd(row.total_cost_usd) }}</td>
              <td class="text-right">{{ formatUsd(row.avg_cost_per_pd_usd) }}</td>
            </tr>
            } @empty {
            <tr><td colspan="6" class="py-6 text-center text-white-dark">No usage in this period.</td></tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <div class="panel">
      <div class="mb-3 flex items-center justify-between">
        <h5 class="text-lg font-semibold">Cost by model</h5>
        <button type="button" [class]="btn.smallSecondary" (click)="exportModels()" [disabled]="!models.length">
          Export CSV
        </button>
      </div>
      <div class="table-responsive">
        <table class="table-hover">
          <thead>
            <tr><th>Model</th><th class="text-right">Calls</th><th class="text-right">Tokens</th><th class="text-right">Cost</th><th class="text-right">Share</th><th class="text-right">Avg / call</th></tr>
          </thead>
          <tbody>
            @for (row of models; track row.model_name) {
            <tr>
              <td>
                <a [routerLink]="['/llm-usage']" [queryParams]="{ model_name: row.model_name }" class="text-primary hover:underline">
                  {{ row.model_name }}
                </a>
              </td>
              <td class="text-right">{{ row.calls | number }}</td>
              <td class="text-right">{{ compactTokens(row.total_tokens) }}</td>
              <td class="text-right font-semibold">{{ formatUsd(row.total_cost_usd) }}</td>
              <td class="text-right">{{ row.cost_pct }}%</td>
              <td class="text-right">{{ formatUsd(row.avg_cost_per_call_usd) }}</td>
            </tr>
            } @empty {
            <tr><td colspan="6" class="py-6 text-center text-white-dark">No usage in this period.</td></tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>
